import { Button, Card, Drawer, IconButton, Stack, Typography } from '@mui/material';
import {
  AnalyticsEventName,
  AttendanceRecordSource,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import {
  ApiError,
  AttendanceCode,
  DATE_FORMAT_DAY,
  DATE_FORMAT_MONTH_YEAR,
  JustificationStatus,
  useGetRelationTrackingPointsAggregateQuery,
  useUpdateAttendanceEntriesForRelation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { JustificationContent } from '@schooly/components/justifications';
import { useNotifications } from '@schooly/components/notifications';
import {
  BellIcon,
  CheckIcon,
  CrossIcon,
  Spin,
  SquareCard,
  TabsWithCounters,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ModalFooter } from '../../../../components/uikit-components/Modal/Modal.styled';
import { ProfileModalMode } from '../../../../context/profile/helpers';
import { allowRenderStudents } from '../../../../helpers/renderRoutesByPermissions';
import { useAttendanceNotificationsEnabled } from '../../../../hooks/useAttendanceNotificationsEnabled';
import { getResultingJustification, getResultingJustificationStatus } from '../../attendance-utils';
import { canEditAttendanceEntries } from '../../permissions';
import { AttendanceGridStudent, AttendanceSidebarData } from '../common/types';
import { AttendanceSidebarPeriods } from './AttendanceSidebarPeriods';
import { AttendanceSidebarEntry, AttendanceSidebarEntrySkeleton } from './AttendanceSidebarEntry';
import {
  AttendanceSidebarFooterBlock,
  AttendanceSidebarJustificationWarning,
} from './AttendanceSidebarWarning';
import {
  AttendanceFormEntries,
  buildFormEntry,
  buildSidebarEntries,
  getJustificationWarningStatus,
  getSessionEntries,
  getSessionEntry,
  getTimetableStructures,
  isPeriodFocus,
  isPeriodOnlyEntry,
  isSessionFocused,
} from './utils';

const SIDEBAR_WIDTH = 400;

enum AttendanceSidebarTab {
  Sessions = 'sessions',
  Periods = 'periods',
}

const SIDEBAR_TABS = [
  { labelId: 'attendance-AmPm', value: AttendanceSidebarTab.Sessions },
  { labelId: 'attendance-PerPeriod', value: AttendanceSidebarTab.Periods },
];

type AttendanceSidebarProps = {
  data: AttendanceSidebarData;
  student: AttendanceGridStudent;
  groupId?: string;
  source: AttendanceRecordSource;
  codesById: Record<string, AttendanceCode>;
  onClose: () => void;
};

export const AttendanceSidebar: FC<AttendanceSidebarProps> = ({
  data,
  student,
  codesById,
  groupId,
  source,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const { permissions, currentStaff, schoolId = '' } = useAuth();
  const { getConfirmation } = useConfirmationDialog();
  const notificationsEnabled = useAttendanceNotificationsEnabled();
  const canViewStudents = allowRenderStudents(permissions);
  const canEdit = canEditAttendanceEntries(permissions);
  const { showError, showNotification } = useNotifications();
  const invalidateAttendanceQueries = useInvalidateListQueriesFor('attendance');
  const { mutateAsync: updateEntries, isLoading: isSaving } =
    useUpdateAttendanceEntriesForRelation();

  const { date, register_id: registerId, relation_id: relationId, focus } = data;

  const { data: aggregate, isLoading: isAggregateLoading } =
    useGetRelationTrackingPointsAggregateQuery(
      { relationId, school_id: schoolId, date_from: date, date_to: date, page_size: 1 },
      { enabled: !!schoolId, refetchOnMount: 'always' },
    );

  const {
    am,
    pm,
    initEntries,
    structures,
    hasPeriodEntries,
    hasSessionEntries,
    justification,
    absenceNotice,
  } = useMemo(() => {
    const weeks = aggregate?.pages.flatMap((page) => page.weeks) ?? [];
    const entries = buildSidebarEntries(weeks);
    const am = getSessionEntry(entries, 'am');
    const pm = getSessionEntry(entries, 'pm');
    const justification = am?.justification ?? pm?.justification ?? null;
    const absenceNotice = am?.absence_request?.comment || pm?.absence_request?.comment;
    const structures = getTimetableStructures(weeks);
    return {
      am,
      pm,
      initEntries: entries,
      structures: getTimetableStructures(weeks),
      hasPeriodEntries: entries.some((entry) => entry.periods.length > 0) && structures.length > 0,
      hasSessionEntries: !!am || !!pm,
      justification,
      absenceNotice,
    };
  }, [aggregate]);

  const periodFocused = focus && isPeriodFocus(focus);
  const initialTab = periodFocused ? AttendanceSidebarTab.Periods : AttendanceSidebarTab.Sessions;
  const [tab, setTab] = useState(initialTab);

  const showTabs = Boolean(hasSessionEntries && hasPeriodEntries);
  const showSessions = Boolean(
    hasSessionEntries && (!showTabs || tab === AttendanceSidebarTab.Sessions),
  );
  const showPeriods = Boolean(
    hasPeriodEntries && (!showTabs || tab === AttendanceSidebarTab.Periods),
  );

  const studentName = getUserFullName(student);
  const hasMultipleSessions = !!am && !!pm;

  const { attendanceDay, attendanceMonthYear } = useMemo(() => {
    const attendanceDate = newDateTimezoneOffset(date);

    return {
      attendanceDay: format(attendanceDate, DATE_FORMAT_DAY),
      attendanceMonthYear: format(attendanceDate, DATE_FORMAT_MONTH_YEAR),
    };
  }, [date]);

  const defaultValues = useMemo<AttendanceFormEntries>(
    () => ({ entries: initEntries.map((entry) => buildFormEntry(entry, groupId)) }),
    [groupId, initEntries],
  );

  const form = useForm<AttendanceFormEntries>({ defaultValues });
  const {
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, dirtyFields },
  } = form;

  // `defaultValues` only seed the form on mount, so re-apply them once the fresh
  // aggregate arrives. `keepDirtyValues` preserves any edits already in progress.
  useEffect(() => {
    reset(defaultValues, { keepDirtyValues: true });
  }, [reset, defaultValues]);

  const entries = watch('entries');
  const justificationNote = watch('justification') ?? null;
  const sessionEntries = getSessionEntries(entries);

  const projectedJustification = getResultingJustification({
    justification,
    note: justificationNote,
    codes: Object.values(codesById),
    entries: sessionEntries,
  });

  const resultingJustification = justification ? projectedJustification : null;
  const justificationWarningStatus = getJustificationWarningStatus(
    justification,
    projectedJustification,
  );

  const showNotificationWarning =
    notificationsEnabled &&
    entries.some((entry, index) => {
      if (isPeriodOnlyEntry(entry)) return false;
      if (!dirtyFields.entries?.[index]?.attendance_code_id) return false;
      const code = entry.attendance_code_id ? codesById[entry.attendance_code_id] : undefined;
      return !!code?.has_push_notifications;
    });
  const showJustificationWarning = !!justificationWarningStatus;

  const onSubmit = useCallback(
    async (values: AttendanceFormEntries) => {
      if (!registerId) return;

      try {
        await updateEntries({ registerId, relationId, data: values });

        const newStatus = getResultingJustificationStatus({
          justification,
          note: values.justification ?? null,
          codes: Object.values(codesById),
          entries: getSessionEntries(values.entries),
        });

        if (newStatus === JustificationStatus.Closed) {
          trackAmplitudeEvent({ name: AnalyticsEventName.AttendanceJustificationClosed });
        } else if (newStatus === JustificationStatus.Pending && justification) {
          trackAmplitudeEvent({ name: AnalyticsEventName.AttendanceJustificationReopen });
        }

        onClose();

        invalidateAttendanceQueries();
        showNotification({ textId: 'attendance-Saved' });
      } catch (error) {
        showError(error as ApiError);
      }
    },
    [
      codesById,
      invalidateAttendanceQueries,
      onClose,
      registerId,
      relationId,
      showError,
      showNotification,
      updateEntries,
      justification,
    ],
  );

  const handleClose = useCallback(async () => {
    if (isSaving) return;

    if (isDirty && !(await getConfirmation({ textId: 'school-edit-CloseUnsavedConfirmation' }))) {
      return;
    }

    onClose();
  }, [getConfirmation, isDirty, isSaving, onClose]);

  const showNoticeOrJustification =
    !isAggregateLoading && (!!absenceNotice || !!resultingJustification);

  const renderContent = () => {
    if (isAggregateLoading) {
      return <AttendanceSidebarEntrySkeleton />;
    }

    return (
      <>
        {showSessions && (
          <>
            {!!am && (
              <AttendanceSidebarEntry
                entry={am}
                student={student}
                justification={justification}
                codesById={codesById}
                date={date}
                source={source}
                collapsible={hasMultipleSessions}
                defaultExpanded={!focus || isSessionFocused(focus, 'am')}
                disabled={isSaving || !canEdit}
              />
            )}
            {!!pm && (
              <AttendanceSidebarEntry
                entry={pm}
                student={student}
                justification={justification}
                codesById={codesById}
                date={date}
                source={source}
                collapsible={hasMultipleSessions}
                defaultExpanded={!focus || isSessionFocused(focus, 'pm')}
                disabled={isSaving || !canEdit}
              />
            )}
          </>
        )}

        {showPeriods && (
          <AttendanceSidebarPeriods
            structures={structures}
            entries={initEntries}
            source={source}
            student={student}
            justification={justification}
            codesById={codesById}
            date={date}
            focus={focus}
            disabled={isSaving || !canEdit}
          />
        )}
      </>
    );
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{ sx: { width: SIDEBAR_WIDTH, maxWidth: '100%' } }}
    >
      <FormProvider {...form}>
        <Stack component="form" onSubmit={handleSubmit(onSubmit)} height="100%">
          <Stack direction="row" alignItems="center" gap={2} p={2.5}>
            <Stack direction="row" gap={2.5} alignItems="center" flex={1} overflow="hidden">
              <SquareCard>
                <Typography mt={0.25} variant="h2" color="primary.main">
                  {attendanceDay}
                </Typography>
                <Typography variant="caption">{attendanceMonthYear}</Typography>
              </SquareCard>

              {studentName && canViewStudents ? (
                <TypographyWithOverflowHint
                  variant="h2"
                  noWrap
                  component={Link}
                  to={{
                    pathname: `/students/${student.relation_id}`,
                    hash: `#${ProfileModalMode.Attendance}`,
                  }}
                  target="_blank"
                  sx={{ '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
                >
                  {studentName}
                </TypographyWithOverflowHint>
              ) : (
                <TypographyWithOverflowHint variant="h2" noWrap>
                  {studentName || '-'}
                </TypographyWithOverflowHint>
              )}
            </Stack>

            <IconButton inverse onClick={handleClose} disabled={isSaving} sx={{ flexShrink: 0 }}>
              <CrossIcon />
            </IconButton>
          </Stack>

          {showNoticeOrJustification && (
            <Stack px={2.5} pb={2.5} gap={1}>
              {!!absenceNotice && (
                <Card sx={{ p: 1 }}>
                  <Stack gap={0.25}>
                    <Typography variant="h4" color="text.secondary">
                      {formatMessage({ id: 'attendance-AbsenceNotice' })}
                    </Typography>
                    <Typography color="primary.main" maxHeight={200} overflow="auto">
                      {absenceNotice}
                    </Typography>
                  </Stack>
                </Card>
              )}

              {!!resultingJustification && (
                <Card sx={{ p: 1 }}>
                  <JustificationContent
                    justification={resultingJustification}
                    pendingNote={
                      justificationNote && currentStaff
                        ? { ...justificationNote, creator: currentStaff }
                        : undefined
                    }
                    relationId={student.relation_id}
                    canViewStudents={canViewStudents}
                  />
                </Card>
              )}
            </Stack>
          )}

          {showTabs && (
            <Stack px={2.5} pb={2.5}>
              <TabsWithCounters
                tabItems={SIDEBAR_TABS}
                activeTab={tab}
                textVariant="h2"
                onChange={(_, nextTab) => setTab(nextTab)}
                sx={{ '& .MuiTab-root:not(:last-child)': { mr: 2.5 } }}
              />
            </Stack>
          )}

          <Stack flex={1} overflow="auto" px={2.5} pb={2.5} gap={2.5}>
            {renderContent()}
          </Stack>

          {isDirty && (
            <ModalFooter
              active
              withBorderTop={false}
              sx={(theme) => ({
                ...theme.mixins.shadowTop(),
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                py: 2,
                gap: 1,
              })}
            >
              {showNotificationWarning && (
                <AttendanceSidebarFooterBlock
                  icon={<BellIcon />}
                  textId="attendance-ParentNotification-OnceSaved"
                  color="common.grey3"
                  bgcolor="common.lightBg"
                />
              )}
              {showJustificationWarning && (
                <AttendanceSidebarJustificationWarning status={justificationWarningStatus} />
              )}
              <Stack direction="row" gap={1}>
                <Button
                  type="submit"
                  disabled={isSaving}
                  startIcon={isSaving ? <Spin /> : <CheckIcon />}
                >
                  <FormattedMessage id="action-Save" />
                </Button>
                <Button type="button" variant="outlined" disabled={isSaving} onClick={handleClose}>
                  <FormattedMessage id="action-Cancel" />
                </Button>
              </Stack>
            </ModalFooter>
          )}
        </Stack>
      </FormProvider>
    </Drawer>
  );
};
